import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/apiClient";
import { useCart } from "./CartContext";
import { toast } from "react-toastify";
import { Link, useLocation } from "react-router-dom";

const Produits = () => {
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [produitsPerPage] = useState(6);
  const [loading, setLoading] = useState({
    produits: true,
    categories: true,
    overall: true,
  });
  const [errors, setErrors] = useState({
    produits: null,
    categories: null,
  });

  // Get addToCart function from CartContext at the top level
  const { addToCart } = useCart();

  // Enhanced error handler
  const handleError = useCallback((error, type) => {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      `Erreur lors du chargement des ${type}`;

    setErrors((prev) => ({
      ...prev,
      [type]: errorMessage,
    }));

    if (error.response?.status >= 500) {
      toast.error(`Problème de connexion au serveur`, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  }, []);

  // Enhanced data fetching
  const fetchProduits = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, produits: true }));
      setErrors((prev) => ({ ...prev, produits: null }));

      const response = await apiClient.get(`/produits`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.data && Array.isArray(response.data)) {
        setProduits(response.data);
      } else {
        throw new Error("Format de données invalide pour les produits");
      }
    } catch (error) {
      handleError(error, "produits");
    } finally {
      setLoading((prev) => ({ ...prev, produits: false }));
    }
  }, [handleError]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, categories: true }));
      setErrors((prev) => ({ ...prev, categories: null }));

      const response = await apiClient.get(`/categories`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        throw new Error("Format de données invalide pour les catégories");
      }
    } catch (error) {
      handleError(error, "categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  }, [handleError]);

  const location = useLocation();

  // On page load, check for category in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryIdFromURL = searchParams.get("category");
    if (categoryIdFromURL) {
      setSelectedCategory(Number(categoryIdFromURL));
      setCurrentPage(1);
    }
  }, [location.search]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading((prev) => ({ ...prev, overall: true }));
        await Promise.all([fetchProduits(), fetchCategories()]);
      } catch (error) {
      } finally {
        setLoading((prev) => ({ ...prev, overall: false }));
      }
    };

    fetchData();
  }, [fetchProduits, fetchCategories]);

  // Filtrage des produits par catégorie
  const filteredProduits = selectedCategory
    ? produits.filter((prod) => {
        // Find the category name for the selected category ID
        const selectedCategoryObj = categories.find(
          (cat) => Number(cat.id_cat) === Number(selectedCategory),
        );

        if (!selectedCategoryObj) return false;

        // Compare by category name since products use nom_cat
        return prod.nom_cat === selectedCategoryObj.nom_cat;
      })
    : produits;

  // Pagination
  const indexOfLastProduct = currentPage * produitsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - produitsPerPage;
  const currentProducts = filteredProduits.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );
  const totalPages = Math.ceil(filteredProduits.length / produitsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 600, behavior: "smooth" });
  };

  // Gestion du changement de catégorie
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  // Fixed: Handle add to cart directly
  const handleAddToCart = (produit) => {
    try {
      addToCart({
        id_prod: produit.id_prod,
        nom_prod: produit.nom_prod,
        prix: produit.prix,
        images: produit.images,
      });
      toast.success(
        <div>
          <strong>{produit.nom_prod}</strong>
          <div>a été ajouté au panier ! ✅</div>
        </div>,
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        },
      );
    } catch (error) {
      toast.error("Erreur lors de l'ajout au panier", {
        position: "bottom-right",
      });
    }
  };

  // Loading component
  const LoadingSpinner = ({ message = "Chargement..." }) => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Chargement...</span>
      </div>
      <p className="mt-3 text-muted">{message}</p>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="text-center py-5">
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {message}
      </div>
      {onRetry && (
        <button className="btn btn-primary mt-3" onClick={onRetry}>
          <i className="fas fa-redo me-2"></i>
          Réessayer
        </button>
      )}
    </div>
  );

  // Main loading state
  if (loading.overall && (loading.produits || loading.categories)) {
    return (
      <main className="main-bg">
        <div className="min-vh-50 d-flex justify-content-center align-items-center">
          <LoadingSpinner message="Chargement du catalogue..." />
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="main-bg">
        {/* Banner Section */}
        <section className="page-banner">
          <div className="page-banner-wrapper p-r z-1">
            <svg
              className="lineanm"
              viewBox="0 0 1920 347"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="line"
                d="M-39 345.187C70 308.353 397.628 293.477 436 145.186C490 -63.5 572 -57.8156 688 255.186C757.071 441.559 989.5 -121.315 1389 98.6856C1708.6 274.686 1940.33 156.519 1964.5 98.6856"
                stroke="white"
                strokeWidth="3"
                strokeDasharray="2 2"
              />
            </svg>
            <div className="page-image">
              <img
                src="assets/images/bg/page-img-1.png"
                alt="Produits"
                onError={(e) => {
                  e.target.src = "assets/images/placeholder.jpg";
                }}
              />
            </div>
            <svg className="page-svg" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21.1742 33.0065C14.029 35.2507 7.5486 39.0636 0 40.7339V86H1937V64.9942C1933.1 60.1623 1912.65 65.1777 1904.51 62.6581C1894.22 59.4678 1884.93 55.0079 1873.77 52.7742C1861.2 50.2585 1823.41 36.3854 1811.99 39.9252C1805.05 42.0727 1796.94 37.6189 1789.36 36.6007C1769.18 33.8879 1747.19 31.1848 1726.71 29.7718C1703.81 28.1919 1678.28 27.0012 1657.53 34.4442C1636.45 42.005 1606.07 60.856 1579.5 55.9191C1561.6 52.5906 1543.41 47.0959 1528.45 56.9075C1510.85 68.4592 1485.74 74.2518 1460.44 76.136C1432.32 78.2297 1408.53 70.6879 1384.73 62.2987C1339.52 46.361 1298.19 27.1677 1255.08 9.28534C1242.58 4.10111 1214.68 15.4762 1200.55 16.6533C1189.77 17.5509 1181.74 15.4508 1172.12 12.8795C1152.74 7.70033 1133.23 2.88525 1111.79 2.63621C1088.85 2.36971 1073.94 7.88289 1056.53 15.8446C1040.01 23.3996 1027.48 26.1777 1007.8 26.1777C993.757 26.1777 975.854 25.6887 962.844 28.9632C941.935 34.2258 932.059 38.7874 914.839 28.6037C901.654 20.8061 866.261 -2.56499 844.356 7.12886C831.264 12.9222 820.932 21.5146 807.663 27.5255C798.74 31.5679 779.299 42.0561 766.33 39.1166C758.156 37.2637 751.815 31.6349 745.591 28.2443C730.967 20.2774 715.218 13.2948 695.846 10.723C676.168 8.11038 658.554 23.1787 641.606 27.4357C617.564 33.4742 602.283 27.7951 579.244 27.7951C568.142 27.7951 548.414 30.4002 541.681 23.6618C535.297 17.2722 530.162 9.74921 523.263 3.71444C517.855 -1.01577 505.798 -0.852017 498.318 2.09709C479.032 9.7007 453.07 10.0516 431.025 9.64475C407.556 9.21163 368.679 1.61612 346.618 10.3636C319.648 21.0575 291.717 53.8338 254.67 45.2266C236.134 40.9201 225.134 37.5813 204.78 40.7339C186.008 43.6415 171.665 50.7785 156.051 57.3567C146.567 61.3523 152.335 52.6281 151.12 47.9222C149.535 41.7853 139.994 34.5585 132.991 30.4008C120.206 22.8098 90.2848 24.3246 74.2546 24.6502C55.5552 25.0301 37.9201 27.747 21.1742 33.0065Z"
                fill="#FFFAF3"
              />
            </svg>
            <div className="shape shape-one">
              <span></span>
            </div>
            <div className="shape shape-two">
              <span></span>
            </div>
            <div className="shape shape-three">
              <span>
                <img
                  src="assets/images/shape/curved-arrow.png"
                  alt="Flèche"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </span>
            </div>
            <div className="shape shape-four">
              <span>
                <img
                  src="assets/images/shape/stars.png"
                  alt="Étoiles"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </span>
            </div>
            <div className="container">
              <div className="row">
                <div className="col-lg-6">
                  <div className="page-banner-content">
                    <h1>Produits</h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Shop Page Section */}
        <section className="shop-page-section pt-120 pb-80">
          <div className="container">
            <div className="row">
              {/* Sidebar Catégories */}
              <div className="col-xl-3">
                <div className="shop-sidebar-area">
                  <div
                    className="product-widget product-categories-widget mb-40"
                    data-aos="fade-up"
                    data-aos-delay="20"
                    data-aos-duration="1000"
                  >
                    <div className="widget-content">
                      <h4 className="widget-title">Catégories</h4>

                      {loading.categories ? (
                        <div className="text-center py-3">
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Chargement...
                            </span>
                          </div>
                          <small className="text-muted d-block mt-2">
                            Chargement des catégories...
                          </small>
                        </div>
                      ) : errors.categories ? (
                        <div className="alert alert-warning py-2">
                          <small>
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            {errors.categories}
                          </small>
                          <button
                            className="btn btn-sm btn-outline-primary mt-2 w-100"
                            onClick={fetchCategories}
                          >
                            <i className="fas fa-redo me-1"></i>
                            Réessayer
                          </button>
                        </div>
                      ) : (
                        <ul className="categories-list">
                          <li
                            className={!selectedCategory ? "active" : ""}
                            onClick={() => handleCategoryChange(null)}
                            style={{ cursor: "pointer", padding: "8px 0" }}
                          >
                            <span>Tous les produits</span>
                            <span className="product-count">
                              ({produits.length})
                            </span>
                          </li>
                          {categories.map((cat) => {
                            const categoryProductCount = produits.filter(
                              (prod) => prod.nom_cat === cat.nom_cat,
                            ).length;

                            return (
                              <li
                                key={cat.id_cat}
                                onClick={() =>
                                  handleCategoryChange(Number(cat.id_cat))
                                }
                                className={
                                  selectedCategory === Number(cat.id_cat)
                                    ? "active"
                                    : ""
                                }
                                style={{ cursor: "pointer", padding: "8px 0" }}
                              >
                                <span>{cat.nom_cat}</span>
                                <span className="product-count">
                                  ({categoryProductCount})
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Zone Produits */}
              <div className="col-xl-9">
                <div className="shop-page-wrapper">
                  {/* Filtre & Affichage */}
                  <div
                    className="shop-filter mb-60"
                    data-aos="fade-up"
                    data-aos-delay="20"
                    data-aos-duration="1000"
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-7 col-12">
                        <div className="show-text">
                          <p>
                            <span>Affichage </span>
                            {loading.produits ? (
                              <span className="text-muted">Chargement...</span>
                            ) : (
                              <>
                                {filteredProduits.length === 0
                                  ? 0
                                  : indexOfFirstProduct + 1}{" "}
                                -{" "}
                                {filteredProduits.length === 0
                                  ? 0
                                  : Math.min(
                                      indexOfLastProduct,
                                      filteredProduits.length,
                                    )}{" "}
                                sur {filteredProduits.length} produits
                                {selectedCategory && (
                                  <span className="ms-2">
                                    <small className="text-muted">
                                      (Catégorie:{" "}
                                      {
                                        categories.find(
                                          (cat) =>
                                            Number(cat.id_cat) ===
                                            selectedCategory,
                                        )?.nom_cat
                                      }
                                      )
                                    </small>
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-3 col-8">
                        <div className="filter-grid-list text-center">
                          <Link to="#" className="active">
                            <i className="far fa-th"></i>
                          </Link>
                          <Link to="#">
                            <i className="far fa-list"></i>
                          </Link>
                        </div>
                      </div>
                      <div className="col-sm-2 col-4">
                        {selectedCategory && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleCategoryChange(null)}
                            title="Réinitialiser le filtre"
                          >
                            <i className="far fa-times"></i> Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Liste des Produits */}
                  <div className="row">
                    {loading.produits ? (
                      <div className="col-12">
                        <LoadingSpinner message="Chargement du catalogue..." />
                      </div>
                    ) : errors.produits ? (
                      <div className="col-12">
                        <ErrorMessage
                          message={errors.produits}
                          onRetry={fetchProduits}
                        />
                      </div>
                    ) : currentProducts.length > 0 ? (
                      currentProducts.map((produit) => (
                        <div
                          key={produit.id_prod}
                          className="col-xl-4 col-md-6 col-sm-12"
                        >
                          <div
                            className="product-item style-one mb-40"
                            data-aos="fade-up"
                            data-aos-delay="25"
                            data-aos-duration="400"
                          >
                            <div className="product-thumbnail">
                              <img
                                className="product-image"
                                src={
                                  produit.images?.[0]
                                    ? `${process.env.REACT_APP_API_URL}/${produit.images[0]}`
                                    : "assets/images/products/feature-product-2.png"
                                }
                                alt={produit.nom_prod}
                                onError={(e) => {
                                  e.target.src =
                                    "assets/images/products/feature-product-2.png";
                                }}
                              />
                              <div className="hover-content">
                                <Link to="#" className="icon-btn">
                                  <i className="fa fa-heart"></i>
                                </Link>
                                <Link
                                  to={
                                    produit.images?.[0]
                                      ? `${process.env.REACT_APP_API_URL}/${produit.images[0]}`
                                      : "#"
                                  }
                                  className="img-popup icon-btn"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fa fa-eye"></i>
                                </Link>
                              </div>
                              <div className="cart-button">
                                <button
                                  className="cart-btn"
                                  onClick={() => handleAddToCart(produit)}
                                >
                                  <i className="far fa-shopping-basket"></i>
                                  <span className="text">
                                    Ajouter au Panier
                                  </span>
                                </button>
                              </div>
                            </div>
                            <div className="product-info-wrap">
                              <div className="product-info">
                                <h4 className="title">
                                  <Link
                                    to={`/produitsDetails/${produit.id_prod}`}
                                  >
                                    {produit.nom_prod || "Produit sans nom"}
                                  </Link>
                                </h4>
                                {produit.prix && (
                                  <div className="price">
                                    <span className="new-price">
                                      {produit.prix}€
                                    </span>
                                    {produit.ancien_prix && (
                                      <span className="old-price">
                                        {produit.ancien_prix}€
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center mt-5">
                        <div className="no-products-found">
                          <i className="far fa-search fa-3x mb-3 text-muted"></i>
                          <h4>Aucun produit trouvé</h4>
                          <p className="text-muted">
                            {selectedCategory
                              ? `Aucun produit disponible dans la catégorie "${
                                  categories.find(
                                    (cat) =>
                                      Number(cat.id_cat) === selectedCategory,
                                  )?.nom_cat
                                }".`
                              : "Aucun produit disponible pour le moment."}
                          </p>
                          {selectedCategory && (
                            <button
                              className="btn btn-primary mt-3"
                              onClick={() => handleCategoryChange(null)}
                            >
                              Voir tous les produits
                            </button>
                          )}
                          {errors.produits && (
                            <button
                              className="btn btn-outline-primary mt-2"
                              onClick={fetchProduits}
                            >
                              <i className="fas fa-redo me-2"></i>
                              Réessayer le chargement
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {!loading.produits &&
                    !errors.produits &&
                    filteredProduits.length > produitsPerPage && (
                      <div className="row">
                        <div className="col-lg-12">
                          <div
                            className="pesco-pagination mb-40"
                            data-aos="fade-up"
                            data-aos-delay="70"
                            data-aos-duration="2200"
                          >
                            <ul>
                              <li>
                                <Link
                                  to="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    paginate(currentPage - 1);
                                  }}
                                  className={
                                    currentPage === 1 ? "disabled" : ""
                                  }
                                >
                                  <i className="far fa-angle-left"></i>
                                </Link>
                              </li>
                              {[...Array(totalPages)].map((_, index) => (
                                <li key={index + 1}>
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      paginate(index + 1);
                                    }}
                                    className={
                                      currentPage === index + 1 ? "active" : ""
                                    }
                                  >
                                    {index + 1}
                                  </Link>
                                </li>
                              ))}
                              <li>
                                <Link
                                  to="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    paginate(currentPage + 1);
                                  }}
                                  className={
                                    currentPage === totalPages ? "disabled" : ""
                                  }
                                >
                                  <i className="far fa-angle-right"></i>
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Produits;
